import TimesIcon from "@/assets/colorable/times.svg?react";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import clsxm from "@/lib/clsxm";
import {ModalContext} from "@/store/modalContext";
import type {TButtonStyle} from "@/store/types";
import last from "lodash/last";
import {useContext, useEffect, useId, type FC, type PropsWithChildren, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {Portal} from "react-portal";
import {useKeyPressEvent, useLockBodyScroll} from "react-use";

const Modal: FC<
  PropsWithChildren & {
    bottomRowClassName?: string;
    onClose(): void;
    onNegativeButtonClick?(): void;
    modalTitle?: string;
    modalTitleClassName?: string;
    onConfirm?(): void;
    closeButtonClassName?: string;
    confirmButtonStyle?: TButtonStyle;
    confirmButtonClassName?: string;
    confirmButtonIcon?: ReactNode;
    confirmButtonText?: string;
    confirmButtonDataTestId?: string;
    confirmButtonTextClassName?: string;
    customBottomRow?: ReactNode;
    customTitleRow?: ReactNode;
    negativeButtonClassName?: string;
    negativeButtonText?: string;
    backgroundClassName?: string;
    className?: string;
    containerClassName?: string;
    isConfirmDisabled?: boolean;
    isLoading?: boolean;
    shouldCloseOnConfirm?: boolean;
    shouldRenderInline?: boolean;
    shouldHideBottomRow?: boolean;
    shouldHideBottomSeparator?: boolean;
    shouldHideCloseButton?: boolean;
    shouldHideHeaderSeparator?: boolean;
    shouldHideModalTitle?: boolean;
    shouldLockBodyScroll?: boolean;
  }
> = ({
  children,
  bottomRowClassName,
  onClose,
  modalTitle,
  modalTitleClassName,
  onConfirm,
  closeButtonClassName,
  confirmButtonStyle,
  confirmButtonClassName,
  confirmButtonIcon,
  confirmButtonText,
  confirmButtonDataTestId,
  confirmButtonTextClassName,
  customBottomRow,
  negativeButtonClassName,
  negativeButtonText,
  onNegativeButtonClick,
  backgroundClassName,
  className,
  containerClassName,
  customTitleRow,
  isConfirmDisabled = false,
  isLoading = false,
  shouldCloseOnConfirm = true,
  shouldRenderInline = false,
  shouldHideBottomRow = false,
  shouldHideBottomSeparator,
  shouldHideCloseButton = false,
  shouldHideHeaderSeparator,
  shouldHideModalTitle = false,
  shouldLockBodyScroll = true,
}) => {
  useLockBodyScroll(shouldLockBodyScroll && !shouldRenderInline);
  const id = useId();
  const {t} = useTranslation("common");
  const close = () => {
    if (!isLoading && !shouldHideCloseButton) onClose();
  };
  const {modalIds, setModalId, deleteModalId} = useContext(ModalContext);
  useEffect(() => {
    if (shouldRenderInline) return;

    setModalId(id);

    return () => {
      deleteModalId(id);
    };
  }, [id, shouldRenderInline]);
  const isLastModal = last(modalIds) === id;
  useKeyPressEvent("Escape", () => {
    if (isLastModal) close();
  });

  const panel = (
    <div
      className={clsxm(
        shouldRenderInline
          ? "flex min-h-0 flex-1 flex-col overflow-hidden"
          : "border-neutral-white-dark flex h-auto max-h-[70vh] w-[80vw] flex-col overflow-auto rounded-md border bg-white shadow-lg md:max-h-[95vh] md:w-4/6",
        backgroundClassName,
      )}>
      {!shouldHideModalTitle && !shouldRenderInline && (
        <div
          className={clsxm(
            "border-neutral-white-dark flex flex-row items-center justify-between",
            !shouldHideHeaderSeparator && "border-b",
          )}>
          {customTitleRow ?? (
            <span className={clsxm("text-neutral-black p-5 text-xl font-semibold", modalTitleClassName)}>
              {modalTitle}
            </span>
          )}
          {!shouldHideCloseButton && (
            <button
              onClick={e => {
                e.stopPropagation();
                close();
              }}
              className={clsxm("mr-5 h-fit", closeButtonClassName)}
              disabled={isLoading}>
              <TimesIcon className="text-neutral-grey-light hover:text-neutral-grey-dark size-5 transition-colors duration-150" />
            </button>
          )}
        </div>
      )}
      <div
        className={clsxm("flex flex-col gap-5 overflow-auto p-5", shouldRenderInline && "min-h-0 flex-1", className)}>
        {children}
      </div>
      {!shouldHideBottomRow &&
        (customBottomRow ?? (
          <div
            className={clsxm(
              "border-neutral-white-dark flex shrink-0 grow gap-3 p-5",
              !shouldHideBottomSeparator && "border-t",
              bottomRowClassName,
            )}>
            <Button
              buttonStyle="White"
              className={clsxm("flex-1", negativeButtonClassName)}
              isDisabled={isLoading}
              onClick={onNegativeButtonClick ?? close}>
              {negativeButtonText ?? t("cancel")}
            </Button>
            <Button
              dataTestId={confirmButtonDataTestId}
              isDisabled={isConfirmDisabled || isLoading}
              onClick={() => {
                onConfirm!();
                if (shouldCloseOnConfirm) close();
              }}
              buttonStyle={confirmButtonStyle ?? "Gradient"}
              className={clsxm("flex-1", confirmButtonClassName)}>
              {isLoading ? (
                <Loader />
              ) : (
                <span
                  className={clsxm(
                    "z-10 inline-flex items-center gap-2 font-medium md:text-lg",
                    confirmButtonTextClassName,
                  )}>
                  {confirmButtonIcon}
                  {confirmButtonText ?? t("confirm")}
                </span>
              )}
            </Button>
          </div>
        ))}
    </div>
  );

  if (shouldRenderInline) return panel;

  return (
    <Portal node={document && document.getElementById("root")}>
      <div
        style={{
          zIndex: 9999 + modalIds.findIndex(modalId => modalId === id),
        }}
        className={clsxm(
          "font-roboto fixed top-1/2 left-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-black/70",
          containerClassName,
        )}
        onMouseDown={({currentTarget, target}) => {
          if (isLastModal && target === currentTarget) close();
        }}>
        {panel}
      </div>
    </Portal>
  );
};

export default Modal;
